import React, { useEffect, useRef, useState } from 'react';
import { Mic, MicOff } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Voice dictation mic button (speech-to-text) using the Web Speech API,
 * supported on Android Chrome, desktop Chrome/Edge and iOS Safari (14.5+).
 * The recognized text is passed to onTranscript so the parent can place it
 * in the message field for the user to review/edit before submitting.
 *
 * Props:
 *  - onTranscript(text): called with the dictated text (interim + final)
 *  - disabled
 *  - size: icon button size classes (optional)
 */
export const VoiceInput = ({ onTranscript, disabled, className = '' }) => {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const supportedRef = useRef(
    typeof window !== 'undefined' &&
    (window.SpeechRecognition || window.webkitSpeechRecognition)
  );

  useEffect(() => () => {
    try { recognitionRef.current?.stop(); } catch (_) { /* noop */ }
  }, []);

  const start = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.lang = 'en-US';
    rec.interimResults = true;
    rec.continuous = false;
    rec.onresult = (e) => {
      let text = '';
      for (let i = 0; i < e.results.length; i += 1) text += e.results[i][0].transcript;
      onTranscript(text.trim());
    };
    rec.onerror = (e) => {
      setListening(false);
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        toast.error('Microphone access was blocked. Enable it in your browser settings.');
      } else if (e.error !== 'aborted' && e.error !== 'no-speech') {
        toast.error('Voice input error. Please try again.');
      }
    };
    rec.onend = () => setListening(false);
    recognitionRef.current = rec;
    try {
      rec.start();
      setListening(true);
    } catch (_) {
      setListening(false);
    }
  };

  const toggle = () => {
    if (!supportedRef.current) {
      toast.error('Voice input isn\u2019t supported on this browser. Try Chrome or Safari.');
      return;
    }
    if (listening) {
      try { recognitionRef.current?.stop(); } catch (_) { /* noop */ }
      setListening(false);
    } else {
      start();
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={disabled}
      aria-label={listening ? 'Stop dictation' : 'Start voice dictation'}
      data-testid="voice-input-btn"
      className={`shrink-0 inline-flex items-center justify-center rounded-md h-10 w-10 transition-colors ${
        listening ? 'bg-rose-500 text-white animate-pulse' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
    >
      {listening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
    </button>
  );
};

export default VoiceInput;
